import * as React from "react";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Switch,
  Tooltip,
  Spin,
} from "antd";
import axios from "axios";
import { responseNotification } from "../../utils/notify";
import { ImageUploader } from "../common";
import {
  QuestionCircleOutlined,
  LeftOutlined,
  CheckCircleFilled,
  EditOutlined,
  BankOutlined,
  MobileOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";

type ShopBank = {
  id: string;
  shopId?: string;
  name?: string;
  bankLogo?: string | null;
  routingNumber?: string | null;
  bankingType: "BANKING" | "MOBILE_BANKING";
  branchName?: string | null;
  accountName?: string;
  accountNumber?: string;
  isDefault?: boolean;
  uploadBankChequeCopy?: string | null;
};

export default function BankDetails() {
  const { token } = useSelector((state) => (state as any)?.authReducer);
  const navigate = useNavigate();

  const [merchantShopCheck, setMerchantShopCheck] = useState<any[]>([]);
  const shopId = merchantShopCheck?.[0]?.id || 0;

  const [banksLoading, setBanksLoading] = useState(false);
  const [shopBanks, setShopBanks] = useState<ShopBank[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ShopBank | null>(null);

  const [bankType, setBankType] = useState<"BANKING" | "MOBILE_BANKING">(
    "BANKING"
  );
  const [mobileBankingType, setMobileBankingType] = useState<string>("BKASH");
  const [confirmLoadingId, setConfirmLoadingId] = useState<string | null>(null);
  const [selectedBankId, setSelectedBankId] = useState<string | null>(null);
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [branchId, setBranchId] = useState<any>();
  const [isDefault, setIsDefault] = useState(false);
  const [generatedImgUrl, setGeneratedImgUrl] = useState<string>();
  const [startUpload, setStartUpload] = useState<
    "Initiated" | "Uploading" | "Uploaded"
  >();

  const [loading, setLoading] = useState(false);
  const [error] = useState("");

  const [form] = Form.useForm();

  const [bankOptions, setBankOptions] = useState<{
    loading: boolean;
    list: any[];
    raw: any[];
  }>({ loading: false, list: [], raw: [] });
  const getMerchantShopCheck = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_CATALOG_READER_API}/merchant/all-shops-by-merchant`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMerchantShopCheck(res?.data?.shops || []);
    } catch (e) {}
  }, [token]);
  const fetchShopBanks = useCallback(async () => {
    if (!shopId) return;
    try {
      setBanksLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_CATALOG_WRITER_API}/shop-bank/getAllBank?shopId=${shopId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShopBanks(res?.data?.shopBanks || []);
    } catch (e) {
      setShopBanks([]);
    } finally {
      setBanksLoading(false);
    }
  }, [shopId, token]);
  const getBankOptions = useCallback(
    async (key?: string) => {
      setBankOptions({ loading: true, list: [], raw: [] });

      const encodedUri = `${process.env.REACT_APP_PAY_API}`;
      let url = `?page=0&limit=20` + (key ? `&key=${key}` : ``);
      url = encodeURI(url);

      try {
        const res = await axios.get(`${encodedUri}/bank${url}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const banks = res.data?.banks || [];
        setBankOptions({
          loading: false,
          raw: banks,
          list: banks.map((bank: any) => ({
            label: bank.name,
            value: bank.id,
          })),
        });
      } catch (err) {
        setBankOptions({ loading: false, list: [], raw: [] });
      }
    },
    [token]
  );
  const onStatusChange = async (id: string, val: boolean) => {
    if (!id) return;
    setShopBanks((prev) => {
      if (!Array.isArray(prev)) return prev;
      if (val) return prev.map((b) => ({ ...b, isDefault: b.id === id }));
      return prev.map((b) => (b.id === id ? { ...b, isDefault: false } : b));
    });

    setConfirmLoadingId(id);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_CATALOG_WRITER_API}/shop-bank/update-bank`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id, isDefault: val }),
        }
      );

      const json = await res.json();

      if (json?.statusCode === 200) {
        responseNotification("Status Changed Successfully", "success");
        await fetchShopBanks();
      } else if (json?.status === 500) {
        responseNotification("Internal server error", "error");
        await fetchShopBanks(); 
      } else {
        responseNotification(json?.message || "something wrong", "warning");
        await fetchShopBanks(); 
      }
    } catch (err) {
      responseNotification(`Internal server error ${err}`, "error");
      await fetchShopBanks();
      console.error("err", err);
    } finally {
      setConfirmLoadingId(null);
    }
  };

  const fetchRef = useRef(0);
  const handleSearch = useMemo(() => {
    const loadOptions = (value: string, field: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      if (fetchId !== fetchRef.current) return;
      if (value && field === "bank") getBankOptions(value);
    };
    return debounce(loadOptions, 700);
  }, [getBankOptions]);

  const handleBankChange = (bankId: string) => {
    setSelectedBankId(bankId);

    const selectedBank = bankOptions.raw.find(
      (bank: any) => bank.id === bankId
    );
    const branches = selectedBank?.branches || [];

    setBranchOptions(
      branches.map((b: any) => ({ label: b.name, value: b.id }))
    );
    setBranchId(undefined);
    form.setFieldsValue({ branchId: undefined });
  };

  const openAdd = () => {
    setEditing(null);
    setOpen(true);

    setBankType("BANKING");
    setMobileBankingType("BKASH");
    setSelectedBankId(null);
    setBranchOptions([]);
    setBranchId(undefined);
    setIsDefault(false);
    setGeneratedImgUrl(undefined);
    setStartUpload(undefined);

    form.resetFields();
    form.setFieldsValue({ bankType: "BANKING", isDefault: false });

    getBankOptions("");
  };

  const openEdit = (item: ShopBank) => {
    setEditing(item);
    setOpen(true);

    const type = (item.bankingType || "BANKING") as
      | "BANKING"
      | "MOBILE_BANKING";
    setBankType(type);
    setIsDefault(!!item.isDefault);
    setGeneratedImgUrl((item.uploadBankChequeCopy as any) || undefined);

    form.resetFields();

    if (type === "MOBILE_BANKING") {
      const mfsName = String(item?.name || "BKASH").toUpperCase();
      setMobileBankingType(mfsName);

      form.setFieldsValue({
        bankType: "MOBILE_BANKING",
        mfsType: mfsName,
        accountNumber: item.accountNumber || "",
        accountName: item.accountName || "",
        isDefault: !!item.isDefault,
      });
    } else {
      getBankOptions("");

      form.setFieldsValue({
        bankType: "BANKING",
        accountName: item.accountName || "",
        accountNumber: item.accountNumber || "",
        routingNumber: item.routingNumber || "",
        isDefault: !!item.isDefault,
      });

      setSelectedBankId(null);
      setBranchId(undefined);
      setBranchOptions([]);
    }
  };

  useEffect(() => {
    if (startUpload === "Uploaded") form.submit();
  }, [startUpload, form]);
  const onSubmit = async (data: any) => {
    try {
      setLoading(true);

      const payload = {
        bankingType: bankType,
        accountName: `${data.accountName || ""}`,
        accountNumber: `${data.accountNumber || ""}`,

        bankName:
          bankType === "MOBILE_BANKING"
            ? mobileBankingType
            : selectedBankId || data.bankId || "",

        branchName:
          bankType === "MOBILE_BANKING" ? "" : branchId || data.branchId || "",

        routingNumber:
          bankType === "MOBILE_BANKING" ? "" : `${data.routingNumber || ""}`,

        isDefault: !!isDefault,
        shopId: shopId,
        uploadBankChequeCopy: generatedImgUrl ? generatedImgUrl : "",
      };

      const url = editing?.id
        ? `${process.env.REACT_APP_CATALOG_WRITER_API}/shop-bank/update-bank`
        : `${process.env.REACT_APP_CATALOG_WRITER_API}/shop-bank/add-bank`;

      const method = editing?.id ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(
          editing?.id ? { ...payload, id: editing.id } : payload
        ),
      });

      const json = await res.json();

      if (json?.statusCode === 200) {
        responseNotification(
          editing?.id ? "Updated Successfully" : "Created Successfully",
          "success"
        );
        setOpen(false);
        setEditing(null);
        form.resetFields();
        setGeneratedImgUrl(undefined);
        setStartUpload(undefined);
        await fetchShopBanks();
      } else {
        responseNotification(json?.message || "something wrong", "warning");
      }
    } catch (e) {
      responseNotification("Internal server error", "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getMerchantShopCheck();
  }, [getMerchantShopCheck]);

  useEffect(() => {
    if (shopId) fetchShopBanks();
  }, [shopId, fetchShopBanks]);
  useEffect(() => {
    if (!open) return;
    if (!editing) return;
    if (bankType !== "BANKING") return;
    if (!bankOptions.raw?.length) return;

    const targetName = String(editing?.name || "").trim().toLowerCase();
    const matchedBank = bankOptions.raw.find(
      (b: any) => String(b?.name || "").trim().toLowerCase() === targetName
    );

    if (matchedBank?.id) {
      setSelectedBankId(matchedBank.id);
      form.setFieldsValue({ bankId: matchedBank.id });

      const branches = matchedBank?.branches || [];
      setBranchOptions(
        branches.map((br: any) => ({ label: br.name, value: br.id }))
      );

      const targetBranchName = String(editing?.branchName || "")
        .trim()
        .toLowerCase();
      const matchedBranch = branches.find(
        (br: any) =>
          String(br?.name || "").trim().toLowerCase() === targetBranchName
      );

      if (matchedBranch?.id) {
        setBranchId(matchedBranch.id);
        form.setFieldsValue({ branchId: matchedBranch.id });
      }
    }
  }, [open, editing, bankType, bankOptions.raw, form]);

  const maskAccount = (v?: string) => {
    if (!v) return "-";
    const s = String(v);
    if (s.length <= 6) return s;
    return `${s.slice(0, 2)}******${s.slice(-3)}`;
  };

  const getTitle = (item: ShopBank) => {
    const n = String(item?.name || "").trim();
    if (n) return n;
    return item.bankingType === "MOBILE_BANKING"
      ? "MOBILE BANKING"
      : "Bank Account";
  };

  const getBadge = (item: ShopBank) =>
    item.bankingType === "MOBILE_BANKING" ? "MFS" : "BANK";

  const getLogoUrl = (item: ShopBank): string | undefined => {
    if (item?.bankLogo) return item.bankLogo as any;

    const n = String(item?.name || "").toUpperCase();
    const mfsMap: Record<string, string> = {
      BKASH: "/assets/mfs/bkash.png",
      NAGAD: "/assets/mfs/nagad.png",
    };
    return mfsMap[n];
  };

  const DefaultSection = ({
    bankId,
    checked,
    loading,
  }: {
    bankId: string;
    checked: boolean;
    loading: boolean;
  }) => {
    return (
      <div className="mt-4">
        <div
          className={[
            "flex items-center justify-between gap-3",
            "px-3 py-2 rounded-xl border",
            checked ? "bg-teal-50 border-teal-200" : "bg-gray-50 border-gray-200",
          ].join(" ")}
        >
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 leading-5">
              Set as Default
            </div>
            <div className="text-xs text-gray-500">
              Default account will be used for payouts.
            </div>
          </div>

          {/* ✅ API call with id + true/false */}
          <Switch
            checked={checked}
            loading={loading}
            disabled={loading}
            onChange={(val) => onStatusChange(bankId, val)}
          />
        </div>
      </div>
    );
  };

  const renderCard = (item: ShopBank) => {
    const isDef = !!item.isDefault;

    const fallbackIcon =
      item.bankingType === "MOBILE_BANKING" ? (
        <MobileOutlined />
      ) : (
        <BankOutlined />
      );

    const logoUrl = getLogoUrl(item);

    return (
      <div
        key={item.id}
        className={[
          "w-full rounded-2xl border bg-white shadow-sm hover:shadow-md transition",
          isDef ? "border-teal-300" : "border-gray-200 hover:border-gray-300",
        ].join(" ")}
      >
        {/* Header */}
        <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            {/* logo/icon box */}
            <div className="relative w-11 h-11 rounded-xl flex items-center justify-center shrink-0 text-white bg-gradient-to-br from-pink-500 to-purple-700 overflow-hidden">
              <span className="text-xl">{fallbackIcon}</span>

              {logoUrl && (
                <img
                  src={logoUrl}
                  alt="bank logo"
                  className="absolute inset-0 m-auto w-7 h-7 object-contain"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
            </div>

            <div className="min-w-0">
              <div className="text-base font-extrabold text-gray-900 leading-5 truncate">
                {getTitle(item)}
              </div>

              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">
                  {getBadge(item)}
                </span>

                {isDef && (
                  <span className="text-xs font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    DEFAULT <CheckCircleFilled className="text-teal-600" />
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <Button
              size="small"
              icon={<EditOutlined />}
              className="!rounded-xl !font-semibold"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openEdit(item);
              }}
            >
              Edit
            </Button>

            <RightOutlined className="text-gray-400" />
          </div>
        </div>

        {/* Body */}
        <div className="px-4 pb-4">
          <div className="border-t border-gray-100 pt-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="space-y-2 text-gray-400">
                <div>Account Name</div>
                <div>
                  {item.bankingType === "MOBILE_BANKING"
                    ? "Mobile Number"
                    : "Account Number"}
                </div>
                {item.bankingType === "BANKING" && <div>Routing Number</div>}
              </div>

              <div className="space-y-2 text-right text-gray-900 font-semibold">
                <div className="truncate">{item.accountName || "-"}</div>
                <div>{maskAccount(item.accountNumber)}</div>
                {item.bankingType === "BANKING" && (
                  <div>{item.routingNumber || "-"}</div>
                )}
              </div>
            </div>

            {item.bankingType === "BANKING" && (
              <div className="mt-3 text-xs text-gray-500">
                <span className="font-semibold">Branch:</span>{" "}
                {item.branchName || "-"}
              </div>
            )}

            {/* ✅ Default switch under branch */}
            <DefaultSection
              bankId={item.id}
              checked={isDef}
              loading={confirmLoadingId === item.id}
            />
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <div className="sticky top-0 z-10 bg-gray-50/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-start gap-3">
            <Button
              type="text"
              icon={<LeftOutlined />}
              onClick={() => navigate(-1)}
              className="!rounded-xl"
            />
            <div>
              <div className="text-xl font-extrabold text-gray-900 leading-6">
                Bank Accounts
              </div>
              <div className="text-xs text-gray-500">
                Add / Edit your shop bank & mobile banking details
              </div>
            </div>
          </div>

          {(shopBanks?.length ?? 0) > 0 && (
            <Button
              type="primary"
              onClick={openAdd}
              className="!bg-rose-600 !border-rose-600 !rounded-xl !font-bold"
            >
              Add More
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-5">
        {banksLoading ? (
          <div className="flex justify-center py-12">
            <Spin />
          </div>
        ) : shopBanks?.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {shopBanks.map(renderCard)}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center">
            <div className="text-lg font-extrabold text-gray-900">
              No bank accounts found
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Add your first bank account to receive payments.
            </div>

            <div className="mt-4">
              <Button
                type="primary"
                onClick={openAdd}
                className="!bg-rose-600 !border-rose-600 !rounded-xl !font-bold"
              >
                Add Bank Account
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <EditOutlined />
            <span>{editing?.id ? "Edit Bank Account" : "Add Bank Account"}</span>
          </div>
        }
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        footer={null}
        destroyOnClose
        centered
      >
        <Form name="bankForm" onFinish={onSubmit} form={form} layout="vertical">
          <div className="flex justify-between border-b pb-3 mb-5 border-gray-100">
            <Form.Item name="bankType" initialValue={bankType} className="mb-0">
              <Radio.Group
                value={bankType}
                onChange={(e) => {
                  const val = e.target.value as "BANKING" | "MOBILE_BANKING";
                  setBankType(val);
                  form.setFieldsValue({ bankType: val });

                  setSelectedBankId(null);
                  setBranchOptions([]);
                  setBranchId(undefined);
                  form.setFieldsValue({
                    bankId: undefined,
                    branchId: undefined,
                    routingNumber: "",
                  });
                }}
              >
                <Radio value="BANKING">BANK</Radio>
                <Radio value="MOBILE_BANKING">MOBILE BANKING (MFS)</Radio>
              </Radio.Group>
            </Form.Item>
          </div>

          {bankType === "MOBILE_BANKING" ? (
            <>
              <Form.Item
                hasFeedback
                label="Mobile Banking Type"
                name="mfsType"
                initialValue={mobileBankingType}
                rules={[
                  { required: true, message: "Mobile Banking Type is required!" },
                ]}
              >
                <Select
                  value={mobileBankingType}
                  onChange={(val) => setMobileBankingType(val)}
                  options={[
                    { value: "BKASH", label: "BKASH" },
                    { value: "NAGAD", label: "NAGAD" },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="accountNumber"
                hasFeedback
                label="Mobile Number"
                rules={[{ required: true, message: "Mobile Number is Required!" }]}
              >
                <Input placeholder="Enter mobile number" />
              </Form.Item>

              <Form.Item
                name="accountName"
                hasFeedback
                label="Full Name"
                rules={[{ required: true, message: "Full Name is Required!" }]}
              >
                <Input placeholder="Enter full name" />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name="accountName"
                hasFeedback
                label="Account Name"
                rules={[{ required: true, message: "Account Name is Required!" }]}
              >
                <Input placeholder="Enter account name" />
              </Form.Item>

              <Form.Item
                name="accountNumber"
                hasFeedback
                label="Account Number"
                rules={[{ required: true, message: "Account Number is Required!" }]}
              >
                <Input placeholder="Enter account number" />
              </Form.Item>

              <Form.Item
                name="bankId"
                label="Bank"
                rules={[{ required: true, message: "Bank is required!" }]}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Select Bank"
                  loading={bankOptions.loading}
                  options={bankOptions.list}
                  value={selectedBankId || undefined}
                  onChange={(val) => handleBankChange(val)}
                  onSearch={(e) => handleSearch(e, "bank")}
                  filterOption={false}
                />
              </Form.Item>

              <Form.Item
                name="branchId"
                label="Branch"
                rules={[{ required: true, message: "Branch is required!" }]}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder={
                    selectedBankId ? "Select Branch" : "Select Bank First"
                  }
                  value={branchId}
                  onChange={(val) => setBranchId(val)}
                  disabled={!selectedBankId}
                  options={branchOptions}
                />
              </Form.Item>

              <Form.Item
                name="routingNumber"
                hasFeedback
                label="Routing Number"
                rules={[{ required: true, message: "Routing Number is Required!" }]}
              >
                <Input placeholder="Enter routing number" />
              </Form.Item>

              <div className="mb-4">
                <Divider orientation="left">
                  Upload Bank Cheque Copy
                  <Tooltip placement="bottom" title="Upload should be clear image">
                    <QuestionCircleOutlined className="px-2 -mt-2" />
                  </Tooltip>
                </Divider>

                <ImageUploader
                  imgUrl={generatedImgUrl || ""}
                  startUpload={startUpload}
                  setStartUpload={(val: any) => setStartUpload(val)}
                  setGeneratedImgUrl={(url: any) => setGeneratedImgUrl(url)}
                />
              </div>
            </>
          )}

          <Form.Item label="Default?" name="isDefault" style={{ minWidth: "100%" }}>
            <Switch
              checkedChildren="Yes"
              unCheckedChildren="No"
              checked={isDefault}
              onChange={setIsDefault}
            />
          </Form.Item>

          <div className="flex gap-3 justify-end mt-6">
            <Button
              onClick={() => {
                form.resetFields();
                setGeneratedImgUrl(undefined);
                setStartUpload(undefined);
                setIsDefault(false);
                setBranchId(undefined);
                setSelectedBankId(null);
              }}
              className="!rounded-xl"
            >
              Reset
            </Button>

            <Button
              type="primary"
              loading={loading}
              disabled={loading || !!error}
              onClick={(e) => {
                e.preventDefault();
                form.submit();
              }}
              className="!bg-rose-600 !border-rose-600 !rounded-xl !font-bold"
              icon={<EditOutlined />}
            >
              {editing?.id ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}